import { Component, OnInit, inject } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { TranslateService } from "@ngx-translate/core";
import { Observable, EMPTY } from "rxjs";
import { catchError, tap } from "rxjs/operators";
import {
  MzTabResult,
  MzTabResultControllerService
} from "../../../../modules/lipidcompass-backend-client";
import { LoggingService, LogLevel } from "../../_services/logging.service";
import { CommonModule } from "@angular/common";
import { DatasetCardComponent } from "../dataset-card/dataset-card.component";
import { ErrorCardComponent } from "../../errors/error-card/error-card.component";
import { DrawerModule } from "primeng/drawer";
import { ShepherdService } from "angular-shepherd";
import { shepherdConfig } from "../../../environments/common";
import { DocViewerComponent } from "../../docs/doc-viewer/doc-viewer.component";
import { waitForElement } from "../../_services/wait-for-element.util";

enum IdTypeEnum {
  NATIVE_ID,
  ID,
  UNKNOWN
}

@Component({
  selector: "app-dataset-detail",
  templateUrl: "./dataset-detail.component.html",
  styleUrls: ["./dataset-detail.component.css"],
  standalone: true,
  imports: [
    CommonModule,
    DatasetCardComponent,
    DocViewerComponent,
    DrawerModule,
    ErrorCardComponent
  ]
})
export class DatasetDetailComponent implements OnInit {
  private mzTabResultControllerService = inject(MzTabResultControllerService);
  private loggingService = inject(LoggingService);

  mzTabResult: Observable<MzTabResult> | undefined;
  errorMessage: string | null = null;
  private mzTabResultId: string | undefined;
  private idType: IdTypeEnum = IdTypeEnum.UNKNOWN;

  protected shepherdService = inject(ShepherdService);

  /** Whether the right-hand help drawer is open. */
  helpVisible: boolean = false;

  /** How long a tour step waits for a lazily-rendered tab panel to appear.
   * Generous for a template render, short enough that a stale selector does
   * not stall the tour. */
  private static readonly TAB_RENDER_TIMEOUT_MS = 3000;

  private static readonly BACK = { text: "Back", type: "back" };
  private static readonly CANCEL = { text: "Cancel", type: "cancel" };
  private static readonly NEXT = { text: "Next", type: "next" };
  private static readonly FINISH = { text: "Finish", type: "next" };

  constructor() {
    const route = inject(ActivatedRoute);
    const translate = inject(TranslateService);

    translate.setDefaultLang("en");
    translate.use("en");
    this.loggingService.log(
      "DatasetDetailComponent",
      "Route snapshot parameters: " + JSON.stringify(route.snapshot.params),
      LogLevel.DEBUG
    );
    if (route.snapshot.params.id !== undefined) {
      this.mzTabResultId = route.snapshot.params.id;
      this.idType = IdTypeEnum.ID;
    } else if (route.snapshot.params.nativeId !== undefined) {
      this.mzTabResultId = route.snapshot.params.nativeId;
      this.idType = IdTypeEnum.NATIVE_ID;
    }
    this.loggingService.log(
      "DatasetDetailComponent",
      "mzTabResultId: " + this.mzTabResultId,
      LogLevel.DEBUG
    );
  }

  ngOnInit(): void {
    switch (this.idType) {
      case IdTypeEnum.ID:
        this.loggingService.log(
          "DatasetDetailComponent",
          "Retrieving mzTabResult by database id: " +
            this.mzTabResultId +
            " ...",
          LogLevel.DEBUG
        );
        if (this.mzTabResultId) {
          this.mzTabResult = this.mzTabResultControllerService
            .mzTabResultGetById(this.mzTabResultId)
            .pipe(
              tap(result => this.buildTour(result)),
              catchError(err => {
                this.loggingService.log(
                  "DatasetDetailComponent",
                  "Error retrieving dataset by id: " + err,
                  LogLevel.ERROR
                );
                this.errorMessage =
                  "It appears the dataset you were trying to access is not yet public or does not exist. If you believe that this is an error, please contact us.";
                return EMPTY;
              })
            );
        }
        break;
      case IdTypeEnum.NATIVE_ID:
        this.loggingService.log(
          "DatasetDetailComponent",
          "Retrieving mzTabResult by native id: " + this.mzTabResultId + " ...",
          LogLevel.DEBUG
        );
        if (this.mzTabResultId) {
          this.mzTabResult = this.mzTabResultControllerService
            .mzTabResultGetByNativeId(this.mzTabResultId)
            .pipe(
              tap(result => this.buildTour(result)),
              catchError(err => {
                this.loggingService.log(
                  "DatasetDetailComponent",
                  "Error retrieving dataset by native id: " + err,
                  LogLevel.ERROR
                );
                this.errorMessage =
                  "It appears the dataset you were trying to access is not yet public or does not exist. If you believe that this is an error, please contact us.";
                return EMPTY;
              })
            );
        }
        break;
      default:
        throw new Error("Unknown id type: " + this.idType);
    }
  }

  /** Configures the shared tour object and loads this page's steps.
   *
   * Unlike the other pages, which build their steps in the constructor, this
   * runs once the dataset has resolved: the step list depends on the dataset's
   * visibility (see tourSteps). ShepherdService.addSteps creates a fresh Tour
   * each call, so nothing accumulates across pages. */
  private buildTour(result: MzTabResult): void {
    this.shepherdService.defaultStepOptions = shepherdConfig.defaultStepOptions;
    this.shepherdService.modal = false;
    this.shepherdService.exitOnEsc = true;
    this.shepherdService.confirmCancel = false;
    this.shepherdService.addSteps(this.tourSteps(result));
  }

  /** Clicks the plot tab strip's own button, then waits for that tab's panel
   * to render and become visible.
   *
   * The `data-testid` attributes on the tab elements are load-bearing in
   * production, not just in tests: driving the real element means the strip's
   * `(click)="!tab.disabled && selectTab(...)"` guard applies, so the tour can
   * never open a tab the page has disabled.
   *
   * The wait requires the panel to be un-hidden, not merely present: each tab
   * panel stays mounted in the DOM behind `[hidden]` once it has been visited
   * (see dataset-detail-plots.component.html), so on a backward step through
   * the tour (e.g. Landscape -> Boxplot) `panelSelector` alone would already match
   * the stale, hidden panel and resolve immediately — anchoring the step to a
   * zero-sized rect. Requiring `:not([hidden])` makes this correct on both
   * the first forward visit and any later revisit. */
  private openTab(index: number, panelSelector: string): () => Promise<unknown> {
    return () => {
      document
        .querySelector<HTMLElement>(`[data-testid="tab-${index}"]`)
        ?.click();
      return waitForElement(
        `${panelSelector}:not([hidden])`,
        DatasetDetailComponent.TAB_RENDER_TIMEOUT_MS
      );
    };
  }

  /** The Landscape step is included only for PUBLIC datasets: Landscape fitting runs
   * on public composition data only, so DatasetDetailPlotsComponent disables
   * that tab otherwise and its panel never renders. */
  private tourSteps(result: MzTabResult): any[] {
    const back = DatasetDetailComponent.BACK;
    const cancel = DatasetDetailComponent.CANCEL;
    const next = DatasetDetailComponent.NEXT;

    const steps: any[] = [
      {
        id: "dataset-detail-welcome",
        text:
          "This is the dataset detail page — the complete record for a single dataset, which in LipidCompass means one mzTab-M result file. It gathers the dataset's metadata, its quantification units, the summary counts from the original file, and a set of interactive plots describing its lipid composition. This tour walks through each part.",
        attachTo: { element: "#app-dataset-detail-component", on: "top" },
        buttons: [cancel, next]
      },
      {
        id: "dataset-detail-status",
        text:
          "The status bar shows who can see this dataset and how far it has travelled through submission. Visibility is Public (open padlock), Restricted, or Private (closed padlock), and the tag beside it is the submission status. Some features — Landscape fitting, for instance — are available for public datasets only.",
        attachTo: { element: "#dataset-detail-status", on: "bottom" },
        buttons: [back, cancel, next]
      },
      {
        id: "dataset-detail-metadata",
        text:
          "The metadata block identifies the dataset: the study it belongs to — follow that link to see the study and its sibling datasets — the title and description, the completeness level indicating which mzTab-M sections are present, the submitting authors, and links to the associated publications by DOI, PubMed, or URL.",
        attachTo: { element: "#dataset-detail-metadata", on: "top" },
        buttons: [back, cancel, next]
      },
      {
        id: "dataset-detail-classifications",
        text:
          "These tags are the dataset's biological classifications — organism, tissue, disease, cell type and other terms annotated with controlled vocabularies. Each tag links out to its ontology entry, so you can confirm exactly which term was used.",
        attachTo: { element: "app-cvparameter-classification", on: "top" },
        buttons: [back, cancel, next]
      },
      {
        id: "dataset-detail-units",
        text:
          "Two quantification units are shown. The database unit is the normalised unit LipidCompass stores, which is what makes datasets comparable with each other; the original unit is what the submitters reported in their mzTab-M file. Both link to their ontology definitions.",
        attachTo: { element: "#dataset-detail-units", on: "top" },
        buttons: [back, cancel, next]
      },
      {
        id: "dataset-detail-stats",
        text:
          "These counts come straight from the mzTab-M file: how many study variable levels, samples, assays and MS runs the dataset covers, and on the right the totals for quantities, summary entries (SML), MS features (SMF) and MS evidence (SME). Together they give you a quick measure of its size and depth.",
        attachTo: { element: "#dataset-detail-stats", on: "top" },
        buttons: [back, cancel, next]
      },
      {
        id: "dataset-detail-lipid-filters",
        text:
          "The Lipid Filters panel restricts the plots below to particular lipid categories, classes or species. It scopes the first three tabs; the Landscape tab always fits the dataset's full composition, whatever the filter says. When a filter is active, a banner appears above the tabs and the affected tabs are flagged with a funnel icon.",
        attachTo: { element: "#dataset-detail-lipid-filters", on: "bottom" },
        buttons: [back, cancel, next]
      },
      {
        id: "dataset-detail-tabs",
        text:
          "The composition plots are grouped into these tabs. Each tab's plots are built the first time you open it, so the page stays responsive — a dot marks a tab you have not visited yet, a spinner marks one still loading, and a greyed-out tab explains itself in its tooltip. The tour will now open each tab in turn.",
        attachTo: { element: "app-responsive-tabs", on: "bottom" },
        buttons: [back, cancel, next]
      },
      {
        id: "dataset-detail-category-class",
        text:
          "The Category & Class Level tab pairs two donuts with two range plots. The upper donut splits the dataset by the number of distinct species per lipid category; the lower one weights each category by reported quantity. Beside them, the range plots show the minimum, mean and maximum quantity across assays — at lipid class level above, at lipid species level below.",
        attachTo: { element: "#category-class-level", on: "top" },
        beforeShowPromise: this.openTab(0, "#category-class-level"),
        buttons: [back, cancel, next]
      },
      {
        id: "dataset-detail-sunburst",
        text:
          "The sunburst charts show the same composition hierarchically, from category through class down to species. The left chart sizes each sector by the number of species it contains, the right one by concentration. Click a sector to zoom into that branch, and click the centre to zoom back out.",
        attachTo: { element: "#species-level-sunburst", on: "top" },
        beforeShowPromise: this.openTab(1, "#species-level-sunburst"),
        buttons: [back, cancel, next]
      },
      {
        id: "dataset-detail-boxplot",
        text:
          "The boxplot shows how each lipid species' quantity is distributed across all of the dataset's sample measurements — the box covers the interquartile range and the line inside it is the median. Enable Show table in the Boxplot Options panel for the same statistics as a sortable, filterable table; filter its rows and click Apply Selection to narrow the plots to those species, or Clear Selection to undo it.",
        attachTo: { element: "#species-level-boxplot", on: "top" },
        beforeShowPromise: this.openTab(2, "#species-level-boxplot"),
        buttons: [back, cancel, next]
      }
    ];

    if (result?.visibility === MzTabResult.VisibilityEnum.PUBLIC) {
      steps.push({
        id: "dataset-detail-landscape",
        text:
          "The Landscape tab places this dataset among all the others in LipidCompass. It fits automatically when you open it — there is nothing to select, because the page already knows which dataset you are viewing. You get the predicted sample labels with confidence bands, the landscape datasets whose lipid profiles are most similar to this one, and the lipids that drove each prediction.",
        attachTo: { element: "#landscape", on: "top" },
        beforeShowPromise: this.openTab(3, "#landscape"),
        buttons: [back, cancel, next]
      });
    }

    steps.push({
      id: "dataset-detail-finish",
      text:
        "That is the dataset detail page. Every other main page has its own feature tour behind the same map-pin button, and the question-mark button beside it opens the written documentation for whichever page you are on.",
      buttons: [back, DatasetDetailComponent.FINISH]
    });

    return steps;
  }
}
